import type { LucideIcon } from 'lucide-react';
import { ArrowRightLeft, Clock, ClockFading, Phone, PhoneOff, TimerReset, UserCheck, Voicemail } from 'lucide-react';
import type { CallStatistic } from 'types/call.type';

export type CallStatusSummaryCardVariant =
    | 'pending'
    | 'overdue'
    | 'inProgress'
    | 'unable'
    | 'contact'
    | 'voicemail'
    | 'transfer'
    | 'avgDuration';

export type StatusIconTheme = {
    icon: LucideIcon;
    color: string;
    softBackground: string;
};

export const STATUS_ICON_THEME: Record<CallStatusSummaryCardVariant, StatusIconTheme> = {
    pending: {
        icon: Clock,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    overdue: {
        icon: ClockFading,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    inProgress: {
        icon: Phone,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    unable: {
        icon: PhoneOff,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    contact: {
        icon: UserCheck,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    voicemail: {
        icon: Voicemail,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    transfer: {
        icon: ArrowRightLeft,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
    avgDuration: {
        icon: TimerReset,
        color: 'var(--slai-ui-primary-purple-dark)',
        softBackground: 'var(--slai-ui-primary-purple-light)',
    },
};

/** Segment shade opacities for stacked progress bars within a category. */
const SEGMENT_OPACITIES = [1, 0.78, 0.62, 0.48, 0.38, 0.26, 0.18];

export const formatCount = (value: number): string => value.toLocaleString();

export const formatChipPercent = (count: number, total: number): string => {
    if (total <= 0) return '0%';
    return `${Math.round((count / total) * 100)}%`;
};

export const getSegmentColor = (baseColor: string, index: number): string => {
    const opacity = SEGMENT_OPACITIES[index] ?? SEGMENT_OPACITIES[SEGMENT_OPACITIES.length - 1];
    return `color-mix(in srgb, ${baseColor} ${Math.round(opacity * 100)}%, transparent)`;
};

export type CallStatusSummarySubStatus = {
    id: string;
    label: string;
    count: number;
    /** Share of the parent category total (0–100). */
    percent: number;
};

export type CallStatusSummaryCard = {
    id: string;
    label: string;
    count: number;
    percent: number;
    variant: CallStatusSummaryCardVariant;
    tooltip: string;
    subStatuses: CallStatusSummarySubStatus[];
};

const EMPTY_CALL_STATISTIC: CallStatistic = {
    pending: 0,
    overDue: 0,
    dialing: 0,
    ringing: 0,
    inProgress: 0,
    transferInProgress: 0,
    humanOnCall: 0,
    agentAbandoned: 0,
    userAbandoned: 0,
    failed: 0,
    busy: 0,
    noAnswer: 0,
    voicemail: 0,
    voicemailSuccess: 0,
    voicemailFailed: 0,
    transferFailed: 0,
    transferCompleted: 0,
    transferUserAbandoned: 0,
    noAgentsAvailable: 0,
    agentRejected: 0,
    total: 0,
    transfer: 0,
    badNumber: 0,
    completed: 0,
    contacted: 0,
    transferCallCenterCompleted: 0,
    transferCallCenterFailed: 0,
    transferCallCenterInProgress: 0,
    blocked: 0,
};

export const formatStatusPercent = (count: number, total: number): number => {
    if (total <= 0) return 0;
    return Math.round((count / total) * 1000) / 10;
};

/** Total calls that were dialed/executed — excludes queued (pending) and overdue. */
export const getExecutedCallsTotal = (callStats: CallStatistic | undefined): number => {
    if (!callStats) return 0;
    return Math.max(0, callStats.total - callStats.pending - callStats.overDue);
};

const resolveUnableToContact = (stats: CallStatistic): number => {
    if (stats.unableToContact != null) return stats.unableToContact;
    return stats.failed + stats.busy + stats.noAnswer + stats.badNumber + stats.blocked;
};

const buildSubStatuses = (
    entries: Array<{ id: string; label: string; count: number }>,
    categoryTotal: number
): CallStatusSummarySubStatus[] =>
    entries.map((entry) => ({
        id: entry.id,
        label: entry.label,
        count: entry.count,
        percent: formatStatusPercent(entry.count, categoryTotal),
    }));

export const buildCallStatusSummaryCards = (
    callStats: CallStatistic | undefined,
    humanTransfer: boolean
): CallStatusSummaryCard[] => {
    const stats = callStats ?? EMPTY_CALL_STATISTIC;
    // Outcome rates are share of executed calls (excludes queued / overdue).
    const executedTotal = getExecutedCallsTotal(stats);
    const percent = (count: number) => formatStatusPercent(count, executedTotal);

    const pendingTotal = stats.pending + stats.overDue;
    const inProgressTotal = stats.ringing + stats.inProgress;
    const unableTotal = resolveUnableToContact(stats);

    const cards: CallStatusSummaryCard[] = [
        {
            id: 'queued',
            label: 'Queued / Overdue',
            count: pendingTotal,
            // Not an executed outcome — rate vs executed is not meaningful.
            percent: 0,
            variant: 'pending',
            tooltip: 'Queued — Calls scheduled for today. Overdue — Calls scheduled for prior days and still not done.',
            subStatuses: buildSubStatuses(
                [
                    { id: 'queued', label: 'Queued', count: stats.pending },
                    { id: 'overdue', label: 'Overdue', count: stats.overDue },
                ],
                pendingTotal
            ),
        },
        {
            id: 'inProgress',
            label: 'In progress',
            count: inProgressTotal,
            percent: percent(inProgressTotal),
            variant: 'inProgress',
            tooltip: 'Current calls with the AI agent.',
            subStatuses: buildSubStatuses(
                [
                    { id: 'ringing', label: 'Ringing', count: stats.ringing },
                    { id: 'active', label: 'Active', count: stats.inProgress },
                ],
                inProgressTotal
            ),
        },
        {
            id: 'unable',
            label: 'Unable to contact',
            count: unableTotal,
            percent: percent(unableTotal),
            variant: 'unable',
            tooltip:
                'Contact was unresponsive and the call was terminated. Bad numbers can be no USA numbers or disconnected lines. Blocked calls have been blocked by the phone carrier.',
            subStatuses: buildSubStatuses(
                [
                    { id: 'failed', label: 'Failed', count: stats.failed },
                    { id: 'busy', label: 'Busy', count: stats.busy },
                    { id: 'noAnswer', label: 'No answer', count: stats.noAnswer },
                    { id: 'badNumber', label: 'Bad number', count: stats.badNumber },
                    { id: 'blocked', label: 'Blocked', count: stats.blocked },
                ],
                unableTotal
            ),
        },
        {
            id: 'contact',
            label: 'Contact',
            count: stats.contacted,
            percent: percent(stats.contacted),
            variant: 'contact',
            tooltip:
                'Contact was responsive. "User Abandoned" and "Agent Abandoned" indicate which party terminated the call. "Completed" reflects calls where the intended purpose was achieved',
            subStatuses: buildSubStatuses(
                [
                    { id: 'userAbandoned', label: 'User abandoned', count: stats.userAbandoned },
                    { id: 'agentAbandoned', label: 'Agent abandoned', count: stats.agentAbandoned },
                    { id: 'completed', label: 'Completed', count: stats.completed },
                    {
                        id: 'transferCallCenterCompleted',
                        label: 'CC transfer completed',
                        count: stats.transferCallCenterCompleted,
                    },
                    {
                        id: 'transferCallCenterFailed',
                        label: 'CC transfer failed',
                        count: stats.transferCallCenterFailed,
                    },
                    {
                        id: 'transferCallCenterInProgress',
                        label: 'CC transfer in progress',
                        count: stats.transferCallCenterInProgress,
                    },
                ],
                stats.contacted
            ),
        },
        {
            id: 'voicemail',
            label: 'Voicemail',
            count: stats.voicemail,
            percent: percent(stats.voicemail),
            variant: 'voicemail',
            tooltip: 'Success - Voicemail was left. Failed - No voicemail could be left.',
            subStatuses: buildSubStatuses(
                [
                    { id: 'success', label: 'Success', count: stats.voicemailSuccess },
                    { id: 'failed', label: 'Failed', count: stats.voicemailFailed },
                ],
                stats.voicemail
            ),
        },
        {
            id: 'transfer',
            label: 'Transfers',
            count: stats.transfer,
            percent: percent(stats.transfer),
            variant: 'transfer',
            tooltip:
                'Calls that are or were transferred to a Human. "No agents available", "Agent rejected" and "Transfer completed" are operational results from Human responses. "Transfer User abandoned" reflects contact drop.',
            subStatuses: buildSubStatuses(
                [
                    { id: 'completed', label: 'Completed', count: stats.transferCompleted },
                    { id: 'failed', label: 'Failed', count: stats.transferFailed },
                    { id: 'inProgress', label: 'In progress', count: stats.transferInProgress },
                    { id: 'noAgents', label: 'No agents', count: stats.noAgentsAvailable },
                    { id: 'agentRejected', label: 'Agent rejected', count: stats.agentRejected },
                    { id: 'userAbandoned', label: 'User abandoned', count: stats.transferUserAbandoned },
                    { id: 'humanOnCall', label: 'Human on call', count: stats.humanOnCall },
                ],
                stats.transfer
            ),
        },
    ];

    return humanTransfer ? cards : cards.filter((card) => card.id !== 'transfer');
};
